import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableColumnHeader } from "@/components/data-table/DataTableColumnHeader";
import { buildDataTableQueryOptions, ensureRouteQueriesData } from "@/lib/route-loader";
import { dataTableFiltersSearchSchema } from "@/lib/route-search";
import { SortDirection } from "@/interfaces/query-options-dto";
import {
  getContactSubmissionsGetContactSubmissionsSummaryQueryOptions,
  useContactSubmissionsGetContactSubmissionsSummary,
  contactSubmissionsGetContactSubmissionsFilterConfig,
  contactSubmissionsGetContactSubmissions,
  getContactSubmissionsGetContactSubmissionsFilterConfigQueryKey,
  getContactSubmissionsGetContactSubmissionsFilterConfigQueryOptions,
} from "@/orval/contact-submissions";
import type { ContactSubmissionSummaryDTO } from "@/orval/model";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Eye, CheckCircle, Clock } from "lucide-react";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { getContactSubmissionStatusLabel, statusIcons, statusClasses } from "./-helpers/contact-submission-helpers";
import { PortalPageHeader } from "@/components/portal-page-header";
import { parseISO } from "date-fns";

export const Route = createFileRoute("/_app/site-admin/contact-submissions")({
  validateSearch: dataTableFiltersSearchSchema,
  loader: ({ location, ...loaderContext }) => {
    const { searchString, filters } = dataTableFiltersSearchSchema.parse(location.search);
    const queryOptions = buildDataTableQueryOptions({
      searchString,
      sortField: "createdAt",
      sortDirection: SortDirection.Descending,
    });

    return ensureRouteQueriesData(loaderContext, [
      getContactSubmissionsGetContactSubmissionsSummaryQueryOptions(),
      getContactSubmissionsGetContactSubmissionsFilterConfigQueryOptions(),
      {
        queryKey: ["site-admin-contact-submissions", queryOptions, filters],
        queryFn: () => contactSubmissionsGetContactSubmissions(filters ?? [], queryOptions),
      },
    ]);
  },
  component: RouteComponent,
});

const getStatusDisplay = (status: number) => {
  const StatusIcon = statusIcons[status as keyof typeof statusIcons];
  const statusText = getContactSubmissionStatusLabel(status);
  return { StatusIcon, statusText };
};

const columns: CustomColumnDef<ContactSubmissionSummaryDTO>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Id" />,
    size: 20,
  },
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    enableSorting: true,
    cell: ({ getValue }) => {
      const name = getValue() as string;
      return <span className="font-medium">{name}</span>;
    },
    size: 80,
  },
  {
    accessorKey: "email",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    enableSorting: true,
    size: 100,
  },
  {
    accessorKey: "messagePreview",
    header: "Message Preview",
    enableSorting: false,
    size: 250,
    cell: ({ getValue }) => {
      const preview = getValue() as string;
      return <span className="text-sm text-muted-foreground max-w-[250px] truncate">{preview}</span>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    enableSorting: true,
    size: 60,
    cell: ({ getValue }) => {
      const status = getValue() as number;
      const { StatusIcon, statusText } = getStatusDisplay(status);
      return (
        <Badge className={`flex items-center gap-1 w-fit ${statusClasses[status as keyof typeof statusClasses]}`}>
          <StatusIcon className="size-3" />
          {statusText}
        </Badge>
      );
    },
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Submitted" />,
    enableSorting: true,
    size: 120,
    cell: ({ getValue }) => {
      const date = getValue() as string;
      return <span className="text-sm text-muted-foreground">{parseISO(date).toLocaleString()}</span>;
    },
  },
];

function RouteComponent() {
  // Use the summary API for counts
  const { data: summary } = useContactSubmissionsGetContactSubmissionsSummary();
  const getContactSubmissions = contactSubmissionsGetContactSubmissions;
  const getContactSubmissionsFilterConfig = contactSubmissionsGetContactSubmissionsFilterConfig;

  const newCount = summary?.newCount ?? 0;
  const readCount = summary?.readCount ?? 0;
  const resolvedCount = summary?.resolvedCount ?? 0;

  return (
    <div className="flex flex-col gap-y-6 p-6">
      <PortalPageHeader
        title="Contact Submissions"
        subtitle="Manage and respond to contact form submissions from your website."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Submissions</CardTitle>
            <Clock className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{newCount}</div>
            <p className="text-xs text-muted-foreground">Awaiting review</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Read</CardTitle>
            <Eye className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{readCount}</div>
            <p className="text-xs text-muted-foreground">Under review</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            <CheckCircle className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resolvedCount}</div>
            <p className="text-xs text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>

      <DataTable
        columns={columns}
        queryFn={(params, abortSignal, filters) => getContactSubmissions(filters ?? [], params, abortSignal)}
        queryKeyFn={(params, filters) => ["site-admin-contact-submissions", params, filters]}
        filtersQueryFn={(abortSignal) => getContactSubmissionsFilterConfig(abortSignal)}
        filtersQueryKeyFn={() => getContactSubmissionsGetContactSubmissionsFilterConfigQueryKey()}
        rowLink={(row) => ({
          from: Route.fullPath,
          to: "$contactSubmissionId",
          params: { contactSubmissionId: row.id },
        })}
        defaultSorting={[{ id: "createdAt", desc: true }]}
        getRowIdFn={(row) => row.id.toString()}
      />

      <Outlet />
    </div>
  );
}
